/**
 * نصائح تقليل الخسائر.
 *
 * لكل نمطٍ من أسباب الخسارة إجراءٌ مختلف: «السعر مرتفع» يقود إلى مراجعة
 * التسعير والباقات، و«انقطع التواصل» يقود إلى انضباط المتابعة. وعرضُ
 * الأسباب مجرّدةً يترك المندوب يعرف *أنه* يخسر ولا يعرف *ماذا يفعل*.
 *
 * ## المطابقة بالكلمات لا بالمفاتيح
 *
 * أسباب الخسارة قائمة قيمٍ تحرّرها كل شركة بنصّها، فلا مفاتيح ثابتة
 * تُطابَق. لذا يُطابَق نصّ السبب بكلماتٍ دالّة — ومن لم يُطابق شيئًا
 * يأخذ النصيحة العامة، فلا يبقى سببٌ بلا إرشاد.
 */

export interface LostCoachTip {
  key: string;
  /** كلمات تدلّ على هذا النمط في نصّ السبب. */
  match: readonly string[];
  title: string;
  /** ما يفعله المندوب — أفعالٌ لا تأمّلات. */
  actions: readonly string[];
}

export const LOST_COACH_TIPS: readonly LostCoachTip[] = [
  {
    key: 'price',
    match: ['سعر', 'غالي', 'مرتفع', 'تكلفة', 'ميزانية', 'price'],
    title: 'الخسارة بسبب السعر',
    actions: [
      'اعرض باقةً أصغر بدل الخصم — النطاق الأقل يحفظ السعر ويفتح الباب.',
      'قسّم الدفع على مراحل: الاعتراض غالبًا على الدفعة لا على المجموع.',
      'اذكر العائد بالأرقام قبل ذكر السعر — القيمة تسبق الرقم.',
      'راجع مع مديرك رصيد خصوماتك قبل الوعد بأي تخفيض.',
    ],
  },
  {
    key: 'competitor',
    match: ['منافس', 'شركة أخرى', 'عرض آخر', 'بديل', 'competitor'],
    title: 'الخسارة لصالح منافس',
    actions: [
      'اسأل الخاسرَ عمّا رجّح المنافس — الإجابة أثمن من الصفقة نفسها.',
      'وثّق ما يميّزنا فعلًا في هذا القطاع، ودرّب عليه في أول لقاء.',
      'اطلب مراجعةً بعد ثلاثة أشهر: كثيرٌ ممن اختار الأرخص يعود.',
    ],
  },
  {
    key: 'silence',
    match: ['تواصل', 'انقطع', 'ردّ', 'رد', 'تجاهل', 'اختفى', 'no response', 'ghost'],
    title: 'انقطاع التواصل',
    actions: [
      'ثبّت متابعةً بموعدٍ في نهاية كل مكالمة — لا تغلق بلا خطوةٍ تالية.',
      'نوّع القناة: من لا يردّ على الهاتف قد يردّ على واتساب أو البريد.',
      'أرسل قيمةً لا تذكيرًا: دراسة حالة أو رقمٌ يخصّ قطاعه.',
      'حدّد سقفًا: بعد ثلاث محاولاتٍ بلا ردّ أغلق الصفقة وحرّر وقتك.',
    ],
  },
  {
    key: 'timing',
    match: ['توقيت', 'وقت', 'مؤجل', 'تأجيل', 'لاحقًا', 'ليس الآن', 'timing'],
    title: 'التوقيت غير مناسب',
    actions: [
      'اسأل عن الموعد المناسب تحديدًا وسجّله مهمةً — لا تتركه «لاحقًا».',
      'اعرف دورة ميزانيتهم: من يشتري في الربع الأول لا يُقنع في الثالث.',
      'أبقِ التواصل خفيفًا حتى موعده بدل الإلحاح الآن.',
    ],
  },
  {
    key: 'fit',
    match: ['غير مناسب', 'لا يحتاج', 'مواصفات', 'متطلبات', 'حجم', 'fit', 'need'],
    title: 'الخدمة لا تناسب حاجته',
    actions: [
      'راجع تأهيل العميل مبكرًا — الخسارة في مرحلة متقدمة تعني تأهيلًا متأخرًا.',
      'دوّن ما طلبه ولم نقدّمه: تكرارُه يعني فجوةً في الكتالوج.',
      'أحِله إن أمكن — من أحلته يذكرك حين تناسبه خدمتك.',
    ],
  },
  {
    key: 'noreason',
    match: ['بلا سبب', 'غير محدد', 'غير مسجَّل', 'أخرى'],
    title: 'خسائر بلا سببٍ مسجَّل',
    actions: [
      'سجّل السبب عند الإغلاق دائمًا — الخسارة بلا سبب لا تُدرَس ولا تُصلَح.',
      'راجع صفقاتك المغلقة بلا سبب وأكملها من ذاكرتك القريبة.',
    ],
  },
];

/** النصيحة العامة لمن لم يُطابق نمطًا معروفًا. */
export const LOST_COACH_FALLBACK: LostCoachTip = {
  key: 'general',
  match: [],
  title: 'مراجعة عامة',
  actions: [
    'اتصل بمن خسرتَه واسأل عن السبب الحقيقي — أغلبهم يجيب.',
    'راجع المرحلة التي تتوقف عندها صفقاتك أكثر، وادرس ما ينقصها.',
  ],
};

/** يطابق نصّ سببٍ بنمطٍ من الأنماط المعروفة. */
export function coachFor(reason: string): LostCoachTip {
  const text = reason.toLowerCase();
  return (
    LOST_COACH_TIPS.find((t) => t.match.some((m) => text.includes(m.toLowerCase()))) ??
    LOST_COACH_FALLBACK
  );
}
